import { MouseEvent, MouseEventHandler, useCallback } from 'react'

import { useCurrentUser, useLoginModal } from '@/core'

type Event = HTMLAnchorElement | HTMLButtonElement | unknown

export type VoidFunction = () => void

type UsePrivateActionReturn = {
  handleLoginModal: (
    action?: MouseEventHandler<Event>,
    /** 匿名ユーザーでも実行できるようにする */
    allowAnonymous?: boolean
  ) => MouseEventHandler<Event> | VoidFunction
  isLoggedIn: boolean
}

/**
 *
 * @description used to handle functions that require the user to be logged in
 * @example
 * const { handleLoginModal } = usePrivateAction()
 * ...
 * <Component {...otherProps} onClick={handleLoginModal()} />
 * ...
 */
export const usePrivateAction = (): UsePrivateActionReturn => {
  const { user } = useCurrentUser()
  const { showModal } = useLoginModal()

  /**
   *
   * 未ログインユーザーの場合にログインモーダルが表示されること。
   * 引数の関数はログインしていた場合に実行されること
   * @param {function} action
   * @return function
   */
  const handleLoginModal = useCallback(
    (action?: (event: MouseEvent<Event>) => void, allowAnonymous = false) =>
      (event: MouseEvent<Event>) => {
        if (allowAnonymous ? !user : !user?.isSigned) {
          event.preventDefault()
          showModal()
          return
        }
        action && action(event)
      },
    [user, showModal]
  )

  return {
    isLoggedIn: !!user?.isSigned,
    handleLoginModal,
  }
}
